import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  PlayIcon,
  StopIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ChartBarIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import DataTable from './DataTable';
import ActionButton from './ActionButton';
import managementService from '../api/managementService';

interface RefreshJob {
  id: string;
  gatewayId: string;
  slaveId: number;
  startDate: string;
  endDate: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progressPercentage: number;
  currentBatchDate?: string;
  totalBatches: number;
  completedBatches: number;
  errorMessage?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  createdBy?: {
    id: string;
    name: string;
    email: string;
  };
}

interface RefreshLog {
  id: string;
  batchDate: string;
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  details?: Record<string, any>;
  executionTimeMs?: number;
  recordsProcessed?: number;
  createdAt: string;
}

interface CreateJobForm {
  startDate: string;
  endDate: string;
  batchSizeDays: number;
  delaySeconds: number;
}

export default function AggregateRefreshJobs() {
  const { } = useTranslation();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isLogsOpen, setIsLogsOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState<CreateJobForm>({
    startDate: '',
    endDate: '',
    batchSizeDays: 1,
    delaySeconds: 5,
  });

  // Fetch jobs with auto-refresh every 5 seconds for active jobs
  const { data: jobsData, isLoading, error } = useQuery({
    queryKey: ['refresh-jobs', currentPage, statusFilter],
    queryFn: () => managementService.aggregateRefresh.getJobs({ 
      page: currentPage, 
      limit: 10,
      status: statusFilter === 'all' ? undefined : statusFilter
    }),
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  // Fetch job logs when modal is open
  const { data: logsData } = useQuery({
    queryKey: ['refresh-job-logs', selectedJobId],
    queryFn: () => selectedJobId ? managementService.aggregateRefresh.getJobLogs(selectedJobId) : null,
    enabled: !!selectedJobId && isLogsOpen,
    refetchInterval: 3000, // Refresh logs every 3 seconds
  });

  // Create job mutation
  const createJobMutation = useMutation({
    mutationFn: (data: CreateJobForm) => managementService.aggregateRefresh.createJob(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refresh-jobs'] });
      setIsCreateOpen(false);
      setCreateForm({
        startDate: '',
        endDate: '',
        batchSizeDays: 1,
        delaySeconds: 5,
      });
    },
  });

  // Cancel job mutation
  const cancelJobMutation = useMutation({
    mutationFn: (jobId: string) => managementService.aggregateRefresh.cancelJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refresh-jobs'] });
    },
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      case 'processing':
        return <PlayIcon className="h-4 w-4 text-blue-500" />;
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      case 'cancelled':
        return <StopIcon className="h-4 w-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level) {
      case 'debug':
        return 'bg-gray-100 text-gray-800';
      case 'info':
        return 'bg-blue-100 text-blue-800';
      case 'warn':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateJob = (e: React.FormEvent) => {
    e.preventDefault();
    createJobMutation.mutate(createForm);
  };

  const handleCancelJob = (jobId: string) => {
    if (window.confirm('Are you sure you want to cancel this job?')) {
      cancelJobMutation.mutate(jobId);
    }
  };

  const handleViewLogs = (jobId: string) => {
    setSelectedJobId(jobId);
    setIsLogsOpen(true);
  };

  const jobColumns = [
    {
      key: 'scope',
      header: 'Scope',
      render: () => (
        <div className="flex items-center">
          <ChartBarIcon className="h-4 w-4 text-gray-400 mr-2" />
          <div>
            <div className="text-sm font-medium text-gray-900">
              All Devices
            </div>
            <div className="text-xs text-gray-500">
              System-wide refresh
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'dateRange',
      header: 'Date Range',
      render: (job: RefreshJob) => (
        <div className="text-sm text-gray-900">
          <div>{new Date(job.startDate).toLocaleDateString()}</div>
          <div className="text-xs text-gray-500">
            to {new Date(job.endDate).toLocaleDateString()}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (job: RefreshJob) => (
        <div className="flex items-center">
          {getStatusIcon(job.status)}
          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
            {job.status}
          </span>
        </div>
      )
    },
    {
      key: 'progress',
      header: 'Progress',
      render: (job: RefreshJob) => (
        <div className="w-full">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>{job.completedBatches}/{job.totalBatches} batches</span>
            <span>{Number(job.progressPercentage).toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Number(job.progressPercentage)}%` }}
            />
          </div>
        </div>
      )
    },
    {
      key: 'created',
      header: 'Created',
      render: (job: RefreshJob) => (
        <div className="text-sm text-gray-900">
          <div>{new Date(job.createdAt).toLocaleDateString()}</div>
          <div className="text-xs text-gray-500">
            {new Date(job.createdAt).toLocaleTimeString()}
          </div>
          {job.createdBy && (
            <div className="text-xs text-gray-400">
              by {job.createdBy.name}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      sticky: 'right' as const,
      width: '120px',
      render: (job: RefreshJob) => (
        <div className="flex items-center space-x-1">
          <ActionButton
            onClick={() => handleViewLogs(job.id)}
            icon={EyeIcon}
            variant="edit"
            title="View Logs"
          />
          {(job.status === 'pending' || job.status === 'processing') && (
            <ActionButton
              onClick={() => handleCancelJob(job.id)}
              icon={StopIcon}
              variant="delete"
              title="Cancel Job"
            />
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Aggregate Refresh Jobs</h1>
          <p className="text-sm text-gray-500">
            Monitor and manage historical data refresh operations
          </p>
        </div>
        <button
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Refresh Job
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Status Filter</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <DataTable
        data={jobsData?.data || []}
        columns={jobColumns}
        isLoading={isLoading}
        error={error}
        pagination={jobsData?.pagination}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        emptyStateIcon={ServerIcon}
        emptyStateTitle="No refresh jobs"
        emptyStateDescription="Create a new refresh job to process historical data."
      />

      {/* Create Job Modal */}
      <Dialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-xl bg-white shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Create Refresh Job
              </DialogTitle>
            </div>

            <div className="px-6 py-4">
              <form onSubmit={handleCreateJob} className="space-y-4">
                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    This will refresh aggregate data for <strong>all devices</strong> in the specified date range.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={createForm.startDate}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={createForm.endDate}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Batch Size (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={createForm.batchSizeDays}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, batchSizeDays: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Delay (seconds)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="60"
                      value={createForm.delaySeconds}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, delaySeconds: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsCreateOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createJobMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {createJobMutation.isPending ? 'Creating...' : 'Create Job'}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Job Logs Modal */}
      <Dialog open={isLogsOpen} onClose={() => setIsLogsOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-4xl h-3/4 rounded-xl bg-white shadow-2xl overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Job Execution Logs
              </DialogTitle>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {logsData?.data && logsData.data.length > 0 ? (
                <div className="space-y-3">
                  {logsData.data.map((log: RefreshLog) => (
                    <div key={log.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLogLevelColor(log.logLevel)}`}>
                            {log.logLevel}
                          </span>
                          <span className="text-sm text-gray-500">
                            {new Date(log.createdAt).toLocaleString()}
                          </span>
                          {log.executionTimeMs && (
                            <span className="text-xs text-gray-400">
                              ({log.executionTimeMs}ms)
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2">
                        <p className="text-sm text-gray-900">{log.message}</p>
                        {log.details && (
                          <pre className="mt-2 text-xs text-gray-600 bg-gray-50 rounded p-2 overflow-x-auto">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No logs available for this job.</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setIsLogsOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}